/* ------------------------------------------------------------------ Shared */
.pickerRoot {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.dateInput,
.timeInput {
  font-variant-numeric: tabular-nums;
}

.time {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.muted {
  color: var(--color-text-subtle);
}

/* ------------------------------------------------------------------ Calendar popover */
.calendar {
  width: 19.5rem;
  min-width: 0;
  padding: var(--space-3);
}

.calendarHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}

.monthLabel {
  color: var(--color-heading);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
}

.navButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
}

.navButton:hover:not(:disabled) {
  background-color: var(--color-surface-hover);
  color: var(--color-text);
}

.navButton:disabled {
  opacity: 0.4;
}

.grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.weekday {
  padding-bottom: var(--space-1);
  color: var(--color-text-subtle);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

.dayCell {
  padding: 1px;
  text-align: center;
}

.day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.day:hover:not([aria-disabled='true']) {
  background-color: var(--color-surface-hover);
}

.day:focus-visible {
  outline-offset: 0;
}

.dayOutside {
  color: var(--color-text-subtle);
}

.dayToday {
  border-color: var(--color-info-border);
  font-weight: var(--font-weight-bold);
}

.daySelected,
.daySelected:hover:not([aria-disabled='true']) {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-primary-contrast);
  font-weight: var(--font-weight-semibold);
}

.day[aria-disabled='true'] {
  color: var(--color-text-disabled);
  text-decoration: line-through;
  cursor: not-allowed;
}

.calendarFooter {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  margin-top: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border);
}

.footerButton {
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  color: var(--color-link);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.footerButton:hover:not(:disabled) {
  background-color: var(--color-surface-hover);
}

.footerButton:disabled {
  color: var(--color-text-disabled);
}

/* ------------------------------------------------------------------ Time listbox */
.timeList {
  max-height: 16rem;
  min-width: 8rem;
  padding: var(--space-1);
}

.timeOption {
  padding: var(--space-1-5) var(--space-2-5);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.timeOption:hover,
.timeOptionActive {
  background-color: var(--color-surface-hover);
}

.timeOptionActive {
  box-shadow: inset 0 0 0 2px var(--color-focus-ring);
}

.timeOptionSelected {
  color: var(--color-link);
  font-weight: var(--font-weight-semibold);
}

/* ------------------------------------------------------------------ DateTimePicker */
.dateTime {
  gap: var(--space-2);
}

.dateTimeRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2) var(--space-3);
}

.dateField {
  flex: 1 1 10rem;
  max-width: 14rem;
}

.timeField {
  flex: 1 1 8rem;
  max-width: 11rem;
}

.zoneLabel {
  display: inline-flex;
  align-items: center;
  min-height: var(--layout-control-height-md);
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.zoneName {
  color: var(--color-text-subtle);
  font-weight: var(--font-weight-regular);
}

.dstNotice {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: 1px solid;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
}

.dstNotice > svg {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.dstWarning {
  border-color: var(--color-warning-border);
  background-color: var(--color-warning-bg);
  color: var(--color-warning-fg);
}

.dstInfo {
  border-color: var(--color-info-border);
  background-color: var(--color-info-bg);
  color: var(--color-info-fg);
}

.dstChoice {
  color: var(--color-text);
}

@media (max-width: 480px) {
  .dateField,
  .timeField {
    max-width: none;
  }
}
